"use client";

/**
 * "Edits · vN" accordion card. Header: version tag, save date, count badges
 * (assumption edits / major revisions / revisions), author avatar stack,
 * chevron. Body: the version's points as a left-railed timeline.
 * Ported from the design's `.acc` / `.hdr-badge` / `.a-tl` classes.
 */

import { useId, useState } from "react";
import { formatDateLabel, formatTimeLabel } from "../lib/time-format";
import { avatarColorClass, avatarInitials } from "./AvatarStack";
import { HistoryPointRow } from "./HistoryPointRow";
import type { HistoryVersionVM } from "./history-types";
import { MATERIAL_CHANGE_TOOLTIP } from "./history-types";

export interface HistoryVersionCardProps {
  version: HistoryVersionVM;
  /** Open on first render (the newest card). Default false. */
  defaultOpen?: boolean;
  /** Passed through to assumption-edit rows' kebab menus. */
  onOpenAssumptions?: () => void;
}

function AuthorAvatar({ name }: { name: string }) {
  return (
    <span
      aria-hidden
      className={[
        "flex h-[22px] w-[22px] items-center justify-center rounded-full",
        "border-2 border-(--bg-white) text-[9.5px] font-bold text-(--fg-primary)",
        avatarColorClass(name),
      ].join(" ")}
    >
      {avatarInitials(name)}
    </span>
  );
}

function CountBadge({
  count,
  singular,
  plural,
  className,
}: {
  count: number;
  singular: string;
  plural: string;
  className: string;
}) {
  if (count === 0) return null;
  return (
    <span
      className={[
        "inline-block rounded-[4px] px-1.5 py-0.5 text-[9.5px] font-bold tracking-wide uppercase",
        className,
      ].join(" ")}
    >
      {count} {count === 1 ? singular : plural}
    </span>
  );
}

export function HistoryVersionCard({
  version,
  defaultOpen = false,
  onOpenAssumptions,
}: HistoryVersionCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  const disclosureId = useId();
  const triggerId = `${disclosureId}-trigger`;
  const panelId = `${disclosureId}-panel`;
  const materialChangeDescriptionId = `${disclosureId}-material-change-description`;

  return (
    <div
      className={[
        "mb-2 overflow-visible rounded-[3px] border bg-(--bg-white)",
        open ? "border-(--border-strong)" : "border-(--border-subtle)",
      ].join(" ")}
    >
      <button
        id={triggerId}
        type="button"
        aria-controls={panelId}
        aria-describedby={
          version.changeKind === "major"
            ? materialChangeDescriptionId
            : undefined
        }
        aria-expanded={open}
        className={[
          "flex w-full flex-wrap items-center gap-x-3.5 gap-y-2 border-0 bg-transparent px-4 py-3.5",
          "cursor-pointer",
          "text-left font-[inherit]",
          "focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--fg-primary)",
        ].join(" ")}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="min-w-[110px] text-[14.5px] font-bold text-(--fg-primary)">
          Edits · v{version.versionNumber}
        </span>
        <span className="min-w-[116px] text-[12.5px] font-medium text-(--fg-secondary)">
          {formatDateLabel(version.createdAt)}
        </span>
        <span className="inline-flex min-w-0 flex-wrap gap-1.5">
          {version.changeKind === "major" &&
          version.badges.majorRevisions === 0 ? (
            <span className="inline-block rounded-[4px] bg-(--empty-bg) px-1.5 py-0.5 text-[9.5px] font-bold tracking-wide text-(--empty) uppercase">
              Material change
            </span>
          ) : null}
          <CountBadge
            count={version.badges.assumptionEdits}
            singular="assumption edit"
            plural="assumption edits"
            className="bg-(--accent-teal-bg) text-(--accent-teal)"
          />
          <CountBadge
            count={version.badges.majorRevisions}
            singular="major revision"
            plural="major revisions"
            className="bg-(--empty-bg) text-(--empty)"
          />
          <CountBadge
            count={version.badges.minorRevisions}
            singular="revision"
            plural="revisions"
            className="bg-(--accent-gold-bg) text-(--accent-gold)"
          />
        </span>
        <span className="ml-auto inline-flex shrink-0 items-center gap-2.5">
          <AuthorAvatar name={version.authorName} />
          <span className="sr-only">Saved by {version.authorName}</span>
          <span
            aria-hidden
            className="text-[12px] font-medium text-(--fg-secondary)"
          >
            Saved by {version.authorName}
          </span>
          <span
            aria-hidden
            className={[
              "text-(--fg-muted) transition-transform",
              open ? "rotate-90" : "",
            ].join(" ")}
          >
            ›
          </span>
        </span>
      </button>
      {version.changeKind === "major" ? (
        <span id={materialChangeDescriptionId} className="sr-only">
          {MATERIAL_CHANGE_TOOLTIP}
        </span>
      ) : null}
      <section
        id={panelId}
        aria-labelledby={triggerId}
        hidden={!open}
        className="border-t border-(--border-subtle) px-4 pt-1 pb-4"
      >
        <div className="relative ml-1.5 border-l-2 border-(--border-subtle) pl-6">
          {version.points.map((point, index) => (
            <HistoryPointRow
              key={`${point.kind}-${index}`}
              point={point}
              actorName={version.authorName}
              timeLabel={formatTimeLabel(version.createdAt)}
              onOpenAssumptions={
                point.kind === "assumption" ? onOpenAssumptions : undefined
              }
            />
          ))}
          {version.points.length === 0 ? (
            <p className="py-2.5 text-[12.5px] text-(--fg-muted) italic">
              No visible changes in this version
              {version.note ? ` — ${version.note}` : ""}.
            </p>
          ) : null}
        </div>
      </section>
    </div>
  );
}
